import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle
} from '../components/ui/card';
import { 
  Plus,
  Edit,
  Save,
  Trash2,
  X
} from 'lucide-react';
import { formatCurrency } from '../utils/chartUtils';
import { clientsAPI, resourcesAPI, sowAPI, actualsAPI } from '../services/api';
import toast from 'react-hot-toast';
import errorHandler from '../utils/errorHandler';

const ActualsManagement = () => {
  const [actualsData, setActualsData] = useState([]);
  const [clients, setClients] = useState([]);
  const [resources, setResources] = useState([]);
  const [sowData, setSowData] = useState([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editingActual, setEditingActual] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    month: '',
    clientId: '',
    resourceId: ''
  });
  const [availableMonths, setAvailableMonths] = useState([]);

  const [newEntry, setNewEntry] = useState({
    client_id: '',
    resource_id: '',
    month_year: '',
    actual_days_worked: 0,
    charge_rate: 0,
    actual_revenue: 0
  });

  // Generate last 12 months dynamically
  const generateLast12Months = () => {
    const months = [];
    const currentDate = new Date();
    
    for (let i = 0; i < 12; i++) {
      const date = new Date(currentDate.getFullYear(), currentDate.getMonth() - i, 1);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const monthName = date.toLocaleString('default', { month: 'long' });
      
      months.push({
        value: `${year}-${month}`,
        label: `${monthName} ${year}`
      });
    }
    
    return months;
  };

  // Get last month as default
  const getLastMonth = () => {
    const currentDate = new Date();
    const lastMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1);
    const year = lastMonth.getFullYear();
    const month = String(lastMonth.getMonth() + 1).padStart(2, '0');
    return `${year}-${month}`;
  };

  useEffect(() => {
    // Initialize months and set default to last month
    const months = generateLast12Months();
    setAvailableMonths(months);
    
    if (!filters.month) {
      const lastMonth = getLastMonth();
      setFilters(prev => ({ ...prev, month: lastMonth }));
    }
    
    if (!newEntry.month_year) {
      const lastMonth = getLastMonth();
      setNewEntry(prev => ({ ...prev, month_year: lastMonth }));
    }
  }, []);

  useEffect(() => {
    if (filters.month) {
      loadFormData();
      loadActualsData();
    }
  }, [filters]);

  // Auto-calculate actual revenue when days worked or charge rate changes
  useEffect(() => {
    const revenue = Math.round(newEntry.actual_days_worked * newEntry.charge_rate);
    setNewEntry(prev => ({ ...prev, actual_revenue: revenue }));
  }, [newEntry.actual_days_worked, newEntry.charge_rate]);

  // Auto-calculate actual revenue when editing actuals
  useEffect(() => {
    if (editingActual) {
      const revenue = Math.round(editingActual.actual_days_worked * editingActual.charge_rate);
      setEditingActual(prev => ({ ...prev, actual_revenue: revenue }));
    }
  }, [editingActual?.actual_days_worked, editingActual?.charge_rate]);

  const loadFormData = async () => {
    try {
      const [clientsResponse, resourcesResponse, sowResponse] = await Promise.all([
        clientsAPI.getAll(),
        resourcesAPI.getAll(),
        sowAPI.getAll()
      ]);
      
      setClients(clientsResponse.data);
      setResources(resourcesResponse.data);
      setSowData(sowResponse.data);
    } catch (error) {
      console.error('Error loading form data:', error);
      toast.error('Failed to load form data');
    }
  };

  const loadActualsData = async () => {
    try {
      setLoading(true);
      const response = await actualsAPI.getAll(filters);
      setActualsData(response.data);
    } catch (error) {
      console.error('Error loading actuals data:', error);
      toast.error('Failed to load actuals data');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!newEntry.client_id || !newEntry.resource_id || !newEntry.month_year) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      const actualsData = {
        clientId: parseInt(newEntry.client_id),
        resourceId: parseInt(newEntry.resource_id),
        monthYear: newEntry.month_year,
        actualDaysWorked: Math.round(parseFloat(newEntry.actual_days_worked) * 100) / 100 || 0,
        chargeRate: Math.round(parseFloat(newEntry.charge_rate)) || 0,
        actualRevenue: Math.round(parseFloat(newEntry.actual_revenue)) || 0
      };
      
      await actualsAPI.create(actualsData);
      toast.success('Actuals entry created successfully');
      setShowAddForm(false);
      setNewEntry({
        client_id: '',
        resource_id: '',
        month_year: '',
        actual_days_worked: 0,
        charge_rate: 0,
        actual_revenue: 0
      });
      loadActualsData();
    } catch (error) {
      console.error('Error creating actuals entry:', error);
      const errorMessage = errorHandler.getErrorMessage(error);
      toast.error('Failed to create actuals entry: ' + errorMessage);
    }
  };

  const handleEdit = (item) => {
    setEditingActual({
      id: item.id,
      client_id: item.client_id || '',
      resource_id: item.resource_id || '',
      month_year: item.month_year,
      actual_days_worked: item.actual_days_worked,
      charge_rate: item.charge_rate,
      actual_revenue: item.actual_revenue
    });
  };

  const handleUpdate = async () => {
    try {
      const actualsData = {
        clientId: parseInt(newEntry.client_id),
        resourceId: parseInt(newEntry.resource_id),
        monthYear: newEntry.month_year,
        actualDaysWorked: Math.round(parseFloat(newEntry.actual_days_worked) * 100) / 100 || 0,
        chargeRate: Math.round(parseFloat(newEntry.charge_rate)) || 0,
        actualRevenue: Math.round(parseFloat(newEntry.actual_revenue)) || 0
      };
      
      await actualsAPI.update(editingId, actualsData);
      toast.success('Actuals entry updated successfully');
      setEditingId(null);
      setNewEntry({
        client_id: '',
        resource_id: '',
        month_year: '',
        actual_days_worked: 0,
        charge_rate: 0,
        actual_revenue: 0
      });
      loadActualsData();
    } catch (error) {
      console.error('Error updating actuals entry:', error);
      const errorMessage = errorHandler.getErrorMessage(error);
      toast.error('Failed to update actuals entry: ' + errorMessage);
    }
  };

  const handleUpdateFromModal = async () => {
    try {
      const actualsData = {
        clientId: parseInt(editingActual.client_id),
        resourceId: parseInt(editingActual.resource_id),
        monthYear: editingActual.month_year,
        actualDaysWorked: Math.round(parseFloat(editingActual.actual_days_worked) * 100) / 100 || 0,
        chargeRate: Math.round(parseFloat(editingActual.charge_rate)) || 0,
        actualRevenue: Math.round(parseFloat(editingActual.actual_revenue)) || 0
      };
      
      await actualsAPI.update(editingActual.id, actualsData);
      toast.success('Actuals entry updated successfully');
      setEditingActual(null);
      loadActualsData();
    } catch (error) {
      console.error('Error updating actuals entry:', error);
      const errorMessage = errorHandler.getErrorMessage(error);
      toast.error('Failed to update actuals entry: ' + errorMessage);
    }
  };

  const handleCancelEdit = () => {
    setEditingActual(null);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this actuals entry?')) {
      try {
        await actualsAPI.delete(id);
        toast.success('Actuals entry deleted successfully');
        loadActualsData();
      } catch (error) {
        console.error('Error deleting actuals entry:', error);
        const errorMessage = errorHandler.getErrorMessage(error);
        toast.error('Failed to delete actuals entry: ' + errorMessage);
      }
    }
  };

  const handleCancel = () => {
    setShowAddForm(false);
    setEditingId(null);
    setNewEntry({
      client_id: '',
      resource_id: '',
      month_year: '',
      actual_days_worked: 0,
      charge_rate: 0,
      actual_revenue: 0
    });
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const getSowDayRate = (clientId, resourceId) => {
    if (!clientId) return 0;
    
    // First, try to find exact client-resource match
    if (resourceId) {
      const sow = sowData.find(s => 
        s.client_id === parseInt(clientId) && 
        s.resource_id === parseInt(resourceId)
      );
      if (sow && sow.day_rate) return sow.day_rate;
    }
    
    // If no exact match, try to find client-resourceType match
    if (resourceId) {
      const selectedResource = resources.find(r => r.id === parseInt(resourceId));
      if (selectedResource && selectedResource.resource_type_id) {
        const sow = sowData.find(s => 
          s.client_id === parseInt(clientId) && 
          s.resource_type_id === parseInt(selectedResource.resource_type_id)
        );
        if (sow && sow.day_rate) return sow.day_rate;
      }
    }
    
    return 0;
  };

  const handleClientChange = (clientId) => {
    setNewEntry(prev => ({ 
      ...prev, 
      client_id: clientId,
      charge_rate: getSowDayRate(clientId, prev.resource_id)
    }));
  };

  const handleResourceChange = (resourceId) => {
    setNewEntry(prev => ({ 
      ...prev, 
      resource_id: resourceId,
      charge_rate: getSowDayRate(prev.client_id, resourceId)
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg">Loading actuals data...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Actuals Management</h1>
          <p className="text-gray-600 mt-1">
            Manually enter actual worked days and charge rates per client/resource/month
          </p>
        </div>
        <button
          onClick={() => setShowAddForm(true)}
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 flex items-center"
        >
          <Plus className="h-4 w-4 mr-2" />
          Add Actuals
        </button>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle>Filters</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex gap-4">
            <select
              value={filters.month}
              onChange={(e) => handleFilterChange('month', e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All Months</option>
              {availableMonths.map(month => (
                <option key={month.value} value={month.value}>{month.label}</option>
              ))}
            </select>
            <select
              value={filters.clientId}
              onChange={(e) => handleFilterChange('clientId', e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All Clients</option>
              {clients.map(client => (
                <option key={client.id} value={client.id}>{client.name}</option>
              ))}
            </select>
            <select
              value={filters.resourceId}
              onChange={(e) => handleFilterChange('resourceId', e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All Resources</option>
              {resources.map(resource => (
                <option key={resource.id} value={resource.id}>{resource.name}</option>
              ))}
            </select>
          </div>
        </CardContent>
      </Card>

      {/* Add/Edit Form */}
      {(showAddForm || editingId) && (
        <Card>
          <CardHeader>
            <CardTitle>{editingId ? 'Edit Actuals Entry' : 'Add Actuals Entry'}</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Client *
                  </label>
                  <select
                    value={newEntry.client_id}
                    onChange={(e) => handleClientChange(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  >
                    <option value="">Select Client</option>
                    {clients.map(client => (
                      <option key={client.id} value={client.id}>{client.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Resource *
                  </label>
                  <select
                    value={newEntry.resource_id}
                    onChange={(e) => handleResourceChange(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  >
                    <option value="">Select Resource</option>
                    {resources.map(resource => (
                      <option key={resource.id} value={resource.id}>{resource.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Month *
                  </label>
                  <select
                    value={newEntry.month_year}
                    onChange={(e) => setNewEntry(prev => ({ ...prev, month_year: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  >
                    <option value="">Select Month</option>
                    {availableMonths.map(month => (
                      <option key={month.value} value={month.value}>{month.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Actual Days Worked *
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={newEntry.actual_days_worked}
                    onChange={(e) => setNewEntry(prev => ({ ...prev, actual_days_worked: parseFloat(e.target.value) || 0 }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    min="0"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Charge Rate (from SOW) *
                  </label>
                  <input
                    type="number"
                    value={newEntry.charge_rate}
                    onChange={(e) => setNewEntry(prev => ({ ...prev, charge_rate: parseFloat(e.target.value) || 0 }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    min="0"
                    step="0.01"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Actual Revenue (Calculated)
                  </label>
                  <input
                    type="number"
                    value={newEntry.actual_revenue}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50"
                    readOnly
                  />
                </div>
              </div>
              <div className="flex justify-end space-x-2">
                <button
                  type="button"
                  onClick={handleCancel}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  Cancel
                </button>
                {editingId ? (
                  <button
                    type="button"
                    onClick={handleUpdate}
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 flex items-center"
                  >
                    <Save className="h-4 w-4 mr-2" />
                    Update
                  </button>
                ) : (
                  <button
                    type="submit"
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 flex items-center"
                  >
                    <Save className="h-4 w-4 mr-2" />
                    Save
                  </button>
                )}
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Actuals Data Table */}
      <Card>
        <CardHeader>
          <CardTitle>Actuals Data</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Client
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Resource
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Resource Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Month
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Planned Days
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    SOW Day Rate
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actual Days
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Charge Rate
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actual Revenue
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {actualsData.map((item) => (
                  <tr key={item.id}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {item.client_name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {item.resource_name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {item.resource_type_name || 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {item.month_year}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {item.planned_days}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatCurrency(item.sow_day_rate)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {parseFloat(item.actual_days_worked).toFixed(2)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatCurrency(item.charge_rate)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {formatCurrency(item.actual_revenue)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleEdit(item)}
                          className="text-blue-600 hover:text-blue-900 flex items-center"
                        >
                          <Edit className="h-4 w-4 mr-1" />
                          Edit
                        </button>
                        <button
                          onClick={() => handleDelete(item.id)}
                          className="text-red-600 hover:text-red-900 flex items-center"
                        >
                          <Trash2 className="h-4 w-4 mr-1" />
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Edit Actuals Modal */}
      {editingActual && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">Edit Actuals Entry</h3>
              <button onClick={handleCancelEdit} className="text-gray-500 hover:text-gray-700">
                <X className="h-6 w-6" />
              </button>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Client</label>
                <select
                  value={editingActual.client_id}
                  onChange={(e) => {
                    const clientId = e.target.value;
                    setEditingActual(prev => ({ 
                      ...prev, 
                      client_id: clientId,
                      charge_rate: getSowDayRate(clientId, prev.resource_id)
                    }));
                  }}
                  className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">Select Client</option>
                  {clients.map(client => (
                    <option key={client.id} value={client.id}>{client.name}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Resource</label>
                <select
                  value={editingActual.resource_id}
                  onChange={(e) => {
                    const resourceId = e.target.value;
                    setEditingActual(prev => ({ 
                      ...prev, 
                      resource_id: resourceId,
                      charge_rate: getSowDayRate(prev.client_id, resourceId)
                    }));
                  }}
                  className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">Select Resource</option>
                  {resources.map(resource => (
                    <option key={resource.id} value={resource.id}>{resource.name}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Month-Year</label>
                <select
                  value={editingActual.month_year}
                  onChange={(e) => setEditingActual({...editingActual, month_year: e.target.value})}
                  className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">Select Month</option>
                  {availableMonths.map(month => (
                    <option key={month.value} value={month.value}>{month.label}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Actual Days Worked</label>
                <input
                  type="number"
                  step="0.01"
                  value={editingActual.actual_days_worked}
                  onChange={(e) => setEditingActual({...editingActual, actual_days_worked: parseFloat(e.target.value) || 0})}
                  className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  min="0"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Charge Rate</label>
                <input
                  type="number"
                  value={editingActual.charge_rate}
                  onChange={(e) => setEditingActual({...editingActual, charge_rate: parseFloat(e.target.value) || 0})}
                  className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  min="0"
                  step="0.01"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Actual Revenue (Calculated)</label>
                <input
                  type="number"
                  value={editingActual.actual_revenue}
                  readOnly
                  className="w-full p-2 border border-gray-300 rounded bg-gray-50 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>

            <div className="flex justify-end space-x-2 mt-6">
              <button
                onClick={handleCancelEdit}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleUpdateFromModal}
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
              >
                <Save className="h-4 w-4 mr-2 inline" />
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ActualsManagement;
